import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/AuthContext';
import axios from '../../auth/axiosConfig';
import { EDIT_MODE_STATUSES, DATA_EDITABLE_STATUSES } from '../../utils/verificationAdmin';

const EditModeContext = createContext(null);

export const useEditMode = () => {
  const context = useContext(EditModeContext);
  if (!context) {
    return { isEditMode: false, canEdit: false, canEditData: false };
  }
  return context;
};

export const EditModeProvider = ({ verificationId, verificationStatus, isFrozen: isFrozenProp, detailData, onDataUpdate, onFrozenChange, children }) => {
  const { tenant } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lockError, setLockError] = useState(null);
  const [changeDescriptions, setChangeDescriptions] = useState([]);
  const originalDataRef = useRef(null);
  const detailDataRef = useRef(detailData);
  detailDataRef.current = detailData;

  const isAdminTenant = tenant?.is_admin_tenant ?? false;
  // canEdit: can enter edit mode (Edit button + action bar). Broad so admins
  // keep access during reprocess (PENDING/RUNNING) and after pivots
  // (NO_MATCH/FAILED).
  const canEdit = isAdminTenant && !isFrozenProp && EDIT_MODE_STATUSES.has(verificationStatus);
  // canEditData: can edit detail_data inline (editors + SaveBar). Narrow to
  // what the backend's PATCH /detail-data accepts — saving on other statuses
  // returns 400 and loses the admin's work.
  const canEditData = canEdit && DATA_EDITABLE_STATUSES.has(verificationStatus);
  const isLive = verificationStatus === 'COMPLETED';
  const isFrozen = !!isFrozenProp;

  const enterEditMode = useCallback(async () => {
    if (!canEdit) return;
    setLockError(null);

    let freshData;
    try {
      const response = await axios.post(`/ui/${verificationId}/edit-lock`);
      if (!response.data.acquired) {
        setLockError(`Being edited by ${response.data.locked_by}`);
        return;
      }
      freshData = response.data.detail_data;
    } catch (err) {
      setLockError(err.response?.data?.detail || 'Failed to acquire edit lock');
      return;
    }

    // Snapshot from the server's current detail_data so edits can't overwrite
    // changes another editor saved between page load and clicking Edit.
    const baseline = JSON.parse(JSON.stringify(freshData ?? detailData));
    if (detailData?._status) baseline._status = detailData._status;
    if (detailData?._is_frozen !== undefined) baseline._is_frozen = detailData._is_frozen;

    if (freshData) onDataUpdate(baseline);
    originalDataRef.current = JSON.parse(JSON.stringify(baseline));
    setChangeDescriptions([]);
    setSaveError(null);
    setIsEditMode(true);
  }, [canEdit, detailData, verificationId, onDataUpdate]);

  const releaseLock = useCallback(async () => {
    try {
      await axios.delete(`/ui/${verificationId}/edit-lock`);
    } catch (err) {
      // Best effort — lock expires after 30 min anyway
    }
  }, [verificationId]);

  const exitEditMode = useCallback(() => {
    setIsEditMode(false);
    setChangeDescriptions([]);
    setSaveError(null);
    setLockError(null);
    releaseLock();
  }, [releaseLock]);

  const discardChanges = useCallback(() => {
    if (originalDataRef.current) {
      onDataUpdate(originalDataRef.current);
    }
    originalDataRef.current = null;
    exitEditMode();
  }, [onDataUpdate, exitEditMode]);

  const addChangeDescription = useCallback((description) => {
    setChangeDescriptions(prev => [...prev, description]);
  }, []);

  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await axios.patch(`/ui/${verificationId}/detail-data`, {
        detail_data: detailDataRef.current,
        edit_description: changeDescriptions.join('; ') || 'Manual edit',
      });
      // Preserve _status and _is_frozen metadata (not returned by PATCH, only by GET /detailed)
      const savedData = { ...response.data };
      if (detailDataRef.current?._status) savedData._status = detailDataRef.current._status;
      if (detailDataRef.current?._is_frozen !== undefined) savedData._is_frozen = detailDataRef.current._is_frozen;
      onDataUpdate(savedData);
      originalDataRef.current = null;
      setIsEditMode(false);
      setChangeDescriptions([]);
      // Lock is released by the backend on successful save
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to save changes';
      setSaveError(msg);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [verificationId, changeDescriptions, onDataUpdate]);

  const unfreezeVerification = useCallback(async (reason) => {
    const response = await axios.post(`/ui/${verificationId}/unfreeze`, { reason });
    if (onFrozenChange) onFrozenChange(false);
    return response.data;
  }, [verificationId, onFrozenChange]);

  const hasChanges = isEditMode && changeDescriptions.length > 0;

  return (
    <EditModeContext.Provider value={{
      verificationId,
      isEditMode,
      canEdit,
      canEditData,
      isAdminTenant,
      isLive,
      isFrozen,
      isSaving,
      saveError,
      clearSaveError: () => setSaveError(null),
      lockError,
      hasChanges,
      changeDescriptions,
      getDetailData: () => detailDataRef.current,
      enterEditMode,
      exitEditMode,
      discardChanges,
      saveChanges,
      addChangeDescription,
      unfreezeVerification,
    }}>
      {children}
    </EditModeContext.Provider>
  );
};
